import { Box } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import {
  attributeTypeOptionsMap,
  isFunctionAttribute,
  isMonetaryValueAttribute,
  type IAttribute,
  isTextAttribute,
  getInitialTextAttribute,
  getInitialFunctionAttribute,
  getInitialMonetaryValueAttribute,
  attributeSchema,
  getInitialNumberAttribute,
  isNumberAttribute,
  getInitialBooleanAttribute,
  getInitialDateAttribute,
  isDateAttribute,
  isBooleanAttribute,
  IAttributeTypeEnum,
  attributeTypeIdOptions,
} from '../../../Interfaces/ILegalTransactionMeasure';
import { useState } from 'react';
import { useAlert } from '../../../context/AlertContext';
import {
  TextAttributeFields,
  FunctionAttributeFields,
  CurrencyAttributeFields,
  NumberAttributeFields,
  DateAttributeFields,
  BooleanAttributeFields,
} from './AttributeFields';

type IProps = {
  initialAttribute?: IAttribute;
  onClose: () => void;
  onCreateOrUpdateAttribute: (attribute: IAttribute) => void;
};

export const CreateOrUpdateAttributeDialog = ({
  initialAttribute,
  onClose,
  onCreateOrUpdateAttribute,
}: IProps) => {
  const { showAlert } = useAlert();
  const [attribute, setAttribute] = useState<IAttribute>(
    initialAttribute || getInitialTextAttribute()
  );

  const handleCreateOrUpdateAttribute = (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    try {
      attributeSchema.parse(attribute);
      onCreateOrUpdateAttribute(attribute);
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Bitte füllen Sie alle Felder korrekt aus.',
        severity: 'error',
      });
    }
  };

  const handleChangeAttributeType = (
    e: SelectChangeEvent<IAttributeTypeEnum>
  ) => {
    const value = e.target.value as IAttributeTypeEnum;

    // Return if the value is the same as the current attribute type
    if (value === attribute.idMeasurementAttributeType) {
      return;
    }

    if (value === IAttributeTypeEnum.text) {
      setAttribute({
        ...getInitialTextAttribute(),
        idMeasurementAttribute: attribute.idMeasurementAttribute,
      });
    } else if (value === IAttributeTypeEnum.function) {
      setAttribute({
        ...getInitialFunctionAttribute(),
        idMeasurementAttribute: attribute.idMeasurementAttribute,
      });
    } else if (value === IAttributeTypeEnum.currency) {
      setAttribute({
        ...getInitialMonetaryValueAttribute(),
        idMeasurementAttribute: attribute.idMeasurementAttribute,
      });
    } else if (value === IAttributeTypeEnum.number) {
      setAttribute({
        ...getInitialNumberAttribute(),
        idMeasurementAttribute: attribute.idMeasurementAttribute,
      });
    } else if (value === IAttributeTypeEnum.boolean) {
      setAttribute({
        ...getInitialBooleanAttribute(),
        idMeasurementAttribute: attribute.idMeasurementAttribute,
      });
    } else if (value === IAttributeTypeEnum.date) {
      setAttribute({
        ...getInitialDateAttribute(),
        idMeasurementAttribute: attribute.idMeasurementAttribute,
      });
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth='sm'>
      <form onSubmit={handleCreateOrUpdateAttribute}>
        <DialogTitle>
          {initialAttribute ? 'Attribut bearbeiten' : 'Attribut hinzufügen'}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '40vh' }}>
          <FormControl variant='outlined' fullWidth sx={{ mt: 1 }}>
            <InputLabel id='attribute-type-label'>
              Attributtyp wählen
            </InputLabel>
            <Select
              fullWidth
              labelId='attribute-type-label'
              label='Attributtyp wählen'
              value={attribute.idMeasurementAttributeType}
              onChange={handleChangeAttributeType}
            >
              {attributeTypeIdOptions().map(option => (
                <MenuItem key={option} value={option}>
                  {attributeTypeOptionsMap[option]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            {isTextAttribute(attribute) ? (
              <TextAttributeFields
                attribute={attribute}
                setAttribute={setAttribute}
              />
            ) : isFunctionAttribute(attribute) ? (
              <FunctionAttributeFields
                attribute={attribute}
                setAttribute={setAttribute}
              />
            ) : isMonetaryValueAttribute(attribute) ? (
              <CurrencyAttributeFields
                attribute={attribute}
                setAttribute={setAttribute}
              />
            ) : isNumberAttribute(attribute) ? (
              <NumberAttributeFields
                attribute={attribute}
                setAttribute={setAttribute}
              />
            ) : isBooleanAttribute(attribute) ? (
              <BooleanAttributeFields
                attribute={attribute}
                setAttribute={setAttribute}
              />
            ) : isDateAttribute(attribute) ? (
              <DateAttributeFields
                attribute={attribute}
                setAttribute={setAttribute}
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant='contained' color='primary' type='submit'>
            {initialAttribute ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
